/**
 * useProjectMoneyProfile.ts — денежный профиль проекта на экране (WEB-2126).
 *
 * Обёртка над `utils/projectMoneyProfile`: объект проекта берётся из стора,
 * конфиг моста — из общего кэша того же помощника (один поход на проект,
 * см. его докблок).
 *
 * ПОЧЕМУ СВЕРЯЕТСЯ `id`. В сторе при переходе между проектами объект
 * обновляется не мгновенно — ровно так же осторожничают денежные разделы
 * («Платежи», `PaymentsTab.tsx`). Без сверки чужой признак мелькнул бы на
 * долю секунды и, например, убрал из меню «Магазин» у проекта, который через
 * нас продаёт.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useAppSelector } from '@/hooks/hooks';
import {
  ensureBridgeConfig,
  isOwnMoney,
  projectMoneyProfile,
  readBridgeConfig,
  subscribeBridgeConfig,
  type ProjectMoneyProfile,
} from '@/utils/projectMoneyProfile';

/** Объект текущего проекта из стора — ровно то, что нужно профилю. */
function useCurrentProject(projectId?: string) {
  const currentProject = useAppSelector(
    (state: { projects?: { currentProject?: { id?: string; ownPayments?: boolean } | null } }) =>
      state.projects?.currentProject ?? null,
  );
  // Сверка `id`: в сторе при переходе между проектами объект обновляется не
  // мгновенно, и чужой признак мелькнул бы на долю секунды.
  return currentProject?.id === projectId ? currentProject : null;
}

/**
 * ТОЛЬКО признак «деньги у клиента, у себя» (WEB-2126).
 *
 * ПОЧЕМУ ОТДЕЛЬНО ОТ ПОЛНОГО ПРОФИЛЯ. Полный профиль ради второго признака
 * (`noBridge`) ходит на сервер за конфигом моста. Большинству экранов —
 * боковому меню, отчётам, «Магазину», поводам писем — нужен ровно один
 * признак, и он уже лежит в объекте проекта. Звать ради него сетевой запрос
 * значило бы дёргать сервер на каждом экране кабинета и ронять проверки,
 * которые про мост ничего не знают.
 */
export function useOwnMoney(projectId?: string): boolean {
  return isOwnMoney(useCurrentProject(projectId));
}

export function useProjectMoneyProfile(projectId?: string): ProjectMoneyProfile {
  const project = useCurrentProject(projectId);

  // Ссылка из кэша стабильна (тот же объект до следующей публикации), поэтому
  // `useSyncExternalStore` не зацикливается на новом снимке каждый рендер.
  const config = useSyncExternalStore(subscribeBridgeConfig, () =>
    projectId ? readBridgeConfig(projectId) : undefined,
  );

  useEffect(() => {
    if (!projectId) return;
    // Отказ глотаем СОЗНАТЕЛЬНО: профиль — это подача, и сетевой сбой конфига
    // моста не повод ронять экран. Он же не запоминается в кэше, поэтому
    // следующий спрашивающий сходит на сервер заново.
    void ensureBridgeConfig(projectId).catch(() => undefined);
  }, [projectId]);

  return projectMoneyProfile(project, config);
}

export default useProjectMoneyProfile;
